import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import FormBg from "../../../Assets/Images/Fundraiser/membershipActivation.svg";
import Profile from "../../../Assets/Images/Profile.svg";
import passwordIcon from "../../../Assets/Icons/passwordIcon.svg";
import TextInput from "../../common/SignUp/TextInput";
import FundraiserHeader from "../common/FundraiserHeader";
import FundraiserBodyHeader from "../common/FundraiserBodyHeader";
import PrimaryButton from "../../common/PrimaryButton";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import { resetPasswordSchema } from "../../../utils/validationSchemas";
import { resetPassword } from "../../../services/authService";
import Loader from "../../common/Loader";
import MultipleAccountsModal from "../../common/ResetPassword/MultipleAccountsModal";

const ResetPassword = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const otpData = useSelector((state) => state?.authReducer?.otpToken);

  const { email } = location?.state || {};

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setConfirmShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [accountConfirmation, setAccountConfirmation] = useState(null);

  const togglePassword = () => setShowPassword(!showPassword);

  const toggleConfirmPassword = () =>
    setConfirmShowPassword(!showConfirmPassword);

  const submitPasswordReset = async (password, confirmAllAccounts = false) => {
    try {
      setIsLoading(true);
      const result = await dispatch(
        resetPassword({
          token: otpData,
          password,
          ...(confirmAllAccounts ? { confirmAllAccounts: true } : {}),
        }),
      ).unwrap();

      if (result?.requiresAccountConfirmation) {
        setAccountConfirmation(result.data);
        return;
      }

      if (result?.success !== true) {
        navigate("/forget-password");
        return;
      }

      await new Promise((resolve) => setTimeout(resolve, 750));
      setIsSuccess(true);
    } catch (err) {
      console.log("err", err);
    } finally {
      setIsLoading(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      password: "",
      confirmPassword: "",
    },
    validationSchema: resetPasswordSchema,
    onSubmit: async (values) => {
      await submitPasswordReset(values.password);
    },
  });

  const { password, confirmPassword } = formik.values;

  const { handleChange, handleBlur, handleSubmit, errors, touched } = formik;

  if (isSuccess) {
    return (
      <div className="pb-24">
        <FundraiserHeader />

        <div className="flex flex-col items-center">
          <FundraiserBodyHeader
            heading={"Reset Successful"}
            content={""}
            contentClass={"w-[100%] sm:w-[80%] md:w-[90%] text-[#8C8C8C]"}
          />

          <div className="relative mt-12 sm:mt-24 mb-14">
            <img
              src={FormBg}
              className="w-[330px] sm:w-[350px]"
              alt="Success Background"
            />

            <div className="absolute top-2 sm:top-0 w-full mt-[-14%]">
              <div className="flex justify-center">
                <div className="w-[80px] h-[80px] sm:w-[104px] sm:h-[104px] rounded-full bg-[#E8F8F0] flex items-center justify-center border-4 border-white shadow-md">
                  <svg
                    className="w-12 h-12 text-[#2ECC71]"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="3"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
              </div>

              <div className="px-[8%] sm:px-[10%] mt-8 text-center font-poppins">
                <h3 className="text-[20px] font-[700] text-[#303030] mb-4">
                  Password Updated!
                </h3>
                <p className="text-[14px] text-[#8C8C8C] leading-relaxed">
                  Your password has been reset successfully.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
    <form className="pb-24" onSubmit={handleSubmit}>
      <FundraiserHeader />

      {isLoading && <Loader />}

      <div className="flex flex-col items-center">
        <FundraiserBodyHeader
          heading={"Enter New Password"}
          content={""}
          contentClass={"w-[100%] sm:w-[80%] md:w-[90%] text-[#8C8C8C]"}
        />

        <div className="relative mt-12 sm:mt-24 mb-14">
          <img src={FormBg} className="w-[330px] sm:w-[350px]"></img>

          <div className="absolute top-2 sm:top-0 w-full mt-[-14%]">
            <div className="flex justify-center">
              <img src={Profile} className="w-[80px] sm:w-[104px]"></img>
            </div>

            <div className="px-[6%] sm:px-[8%] mt-5">
              <TextInput
                Icon={passwordIcon}
                type={showPassword ? "text" : "password"}
                name={"password"}
                value={password}
                placeholder={"Enter Password "}
                inputClass={
                  "w-full rounded-tr-[10px] rounded-br-[10px] border-none pl-4 pr-10 bg-white"
                }
                iconAreaClass={
                  "w-[18%] bg-[#E1E1E1] rounded-tl-[10px] rounded-bl-[10px] border-none"
                }
                inputDivClass={"mb-3 sm:mb-2"}
                handleChange={handleChange}
                handleBlur={handleBlur}
                error={errors.password}
                touched={touched.password}
                showPassword={showPassword}
                togglePassword={togglePassword}
                autoComplete={"new-password"}
              />

              <TextInput
                Icon={passwordIcon}
                type={showConfirmPassword ? "text" : "password"}
                name={"confirmPassword"}
                value={confirmPassword}
                placeholder={"Confirm Password"}
                inputClass={
                  "w-full rounded-tr-[10px] rounded-br-[10px] border-none pl-4 pr-10 bg-white"
                }
                iconAreaClass={
                  "w-[18%] bg-[#E1E1E1] rounded-tl-[10px] rounded-bl-[10px] border-none"
                }
                inputDivClass={"mb-3 sm:mb-2"}
                handleChange={handleChange}
                handleBlur={handleBlur}
                error={errors.confirmPassword}
                touched={touched.confirmPassword}
                showPassword={showConfirmPassword}
                togglePassword={toggleConfirmPassword}
              />

              <PrimaryButton
                type="submit"
                className={
                  "mt-5 w-[100%] text-[18px] font-[600] h-[50px] sm:h-[45px] md:h-[50px] rounded-[10px]"
                }
                label="Submit"
              />
            </div>
          </div>
        </div>
      </div>
    </form>
    {accountConfirmation && (
      <MultipleAccountsModal
        email={accountConfirmation.email || email}
        accountCount={accountConfirmation.accountCount}
        roles={accountConfirmation.roles}
        loading={isLoading}
        onCancel={() => setAccountConfirmation(null)}
        onContinue={() =>
          submitPasswordReset(formik.values.password, true)
        }
      />
    )}
    </>
  );
};

export default ResetPassword;
